'use client';

import React, { useRef, useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { submitContact } from '@/lib/data/contact';
import { useTranslation } from '@/lib/i18n/useTranslation';

gsap.registerPlugin(ScrollTrigger);

const GOLD = '#D48937';

export default function Contact() {
    const containerRef = useRef(null);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const { t, locale } = useTranslation();

    useGSAP(() => {
        const mm = gsap.matchMedia();
        mm.add('(prefers-reduced-motion: no-preference)', () => {
            const tl = gsap.timeline({ defaults: { ease: 'expo.out' } });
            tl.from('[data-contact-eyebrow]', { autoAlpha: 0, y: 12, duration: 1 }, 0)
              .from('[data-contact-title]', { autoAlpha: 0, x: -32, duration: 1.4 }, 0.1)
              .from('[data-contact-rule]', { scaleX: 0, transformOrigin: 'left center', duration: 1.2 }, 0.3)
              .from('[data-contact-left]', { autoAlpha: 0, y: 24, duration: 1.2 }, 0.4)
              .from('[data-contact-right]', { autoAlpha: 0, y: 24, duration: 1.2 }, 0.55);
        });
    }, { scope: containerRef });

    const handleSubmit = async (event) => {
        event.preventDefault();
        const formData = new FormData(event.target);
        try {
            await submitContact({
                name: formData.get('name'),
                email: formData.get('email'),
                message: formData.get('message'),
                subject: 'Kontaktanfrage Website',
            });
            setIsSubmitted(true);
        } catch (error) {
            console.error('Error submitting contact form:', error);
            alert(t('contact.error'));
        }
    };

    return (
        <>
            <div
                ref={containerRef}
                className="relative z-20 min-h-screen bg-gradient-to-br from-white to-gray-200 text-neutral-800"
            >
                <Navbar />

                <main className="px-6 pb-32 pt-28 md:px-12 md:pt-24 lg:px-20">
                    <div key={locale} className="mx-auto max-w-6xl">

                    {/* Header */}
                    <header className="mb-10 md:mb-12">
                        <p
                            data-contact-eyebrow
                            className="mb-3 text-[10px] uppercase tracking-[0.35em]"
                            style={{ color: GOLD }}
                        >
                            {t('contact.eyebrow')}
                        </p>
                        <h1
                            data-contact-title
                            className="font-pp-neue text-[clamp(2.5rem,6vw,5rem)] font-normal leading-none tracking-tight text-neutral-900"
                        >
                            {t('contact.title')}
                        </h1>
                        <div
                            data-contact-rule
                            className="mt-5 h-px w-full"
                            style={{ background: `linear-gradient(to right, ${GOLD}, transparent)` }}
                        />
                    </header>

                    {/* Body grid */}
                    <div className="grid gap-12 md:grid-cols-12 md:gap-16">

                        {/* Form — top-left on desktop */}
                        <div
                            data-contact-left
                            className="md:col-span-7 md:col-start-1 md:row-start-1"
                        >
                            <p className="mb-8 max-w-prose text-sm leading-relaxed text-neutral-600 md:text-base">
                                {t('contact.description')}
                            </p>

                            {isSubmitted ? (
                                <div className="space-y-10 py-4">
                                    <div>
                                        <p
                                            className="font-pp-neue text-[clamp(2rem,5vw,3.5rem)] font-normal leading-none tracking-tight text-neutral-900"
                                        >
                                            {t('contact.thankYou')}
                                        </p>
                                        <div
                                            className="mt-4 h-px w-16"
                                            style={{ background: GOLD }}
                                        />
                                        <p className="mt-6 text-sm leading-relaxed text-neutral-500">
                                            {t('contact.messageSent')}
                                        </p>
                                    </div>
                                    <Link
                                        href="/"
                                        className="inline-block text-[10px] uppercase tracking-[0.3em] text-neutral-800 underline-offset-4 hover:underline"
                                    >
                                        {t('contact.backToHome')}
                                    </Link>
                                </div>
                            ) : (
                                <form onSubmit={handleSubmit} className="space-y-5">
                                    {[
                                        { id: 'name',    type: 'text',  label: t('contact.fields.name'),    placeholder: t('contact.fields.placeholder.name') },
                                        { id: 'email',   type: 'email', label: t('contact.fields.email'),   placeholder: t('contact.fields.placeholder.email') },
                                    ].map(({ id, type, label, placeholder }) => (
                                        <div key={id} className="group relative">
                                            <label
                                                htmlFor={id}
                                                className="mb-1.5 block text-[10px] uppercase tracking-[0.3em] text-neutral-500"
                                            >
                                                {label}
                                            </label>
                                            <input
                                                type={type}
                                                id={id}
                                                name={id}
                                                placeholder={placeholder}
                                                required
                                                className="w-full border-0 border-b border-neutral-300 bg-transparent py-2 text-sm text-neutral-900 placeholder-neutral-400 transition-colors focus:border-neutral-800 focus:outline-none"
                                            />
                                        </div>
                                    ))}

                                    <div className="group relative">
                                        <label
                                            htmlFor="message"
                                            className="mb-1.5 block text-[10px] uppercase tracking-[0.3em] text-neutral-500"
                                        >
                                            {t('contact.fields.message')}
                                        </label>
                                        <textarea
                                            id="message"
                                            name="message"
                                            rows={3}
                                            placeholder={t('contact.fields.placeholder.message')}
                                            required
                                            className="w-full resize-none border-0 border-b border-neutral-300 bg-transparent py-2 text-sm text-neutral-900 placeholder-neutral-400 transition-colors focus:border-neutral-800 focus:outline-none"
                                        />
                                    </div>

                                    <div className="pt-1">
                                        <button
                                            type="submit"
                                            className="text-[11px] uppercase tracking-[0.25em] border border-neutral-800 px-8 py-3 text-neutral-800 transition-colors duration-300 hover:bg-neutral-800 hover:text-white focus:outline-none"
                                        >
                                            {t('contact.send')}
                                        </button>
                                    </div>
                                </form>
                            )}
                        </div>

                        {/* Image — full right column, spans both rows on desktop */}
                        <div
                            data-contact-right
                            className="md:col-span-5 md:col-start-8 md:row-start-1 md:row-span-2"
                        >
                            <div className="relative aspect-[4/3] w-full overflow-hidden md:aspect-auto md:h-full md:min-h-[560px]">
                                <Image
                                    src="/images/contact/contact1.jpeg"
                                    alt="Atelier Donata Oppermann"
                                    fill
                                    style={{ objectFit: 'cover', objectPosition: 'center top' }}
                                    sizes="(max-width: 768px) 100vw, 40vw"
                                    priority
                                />
                            </div>
                        </div>

                        {/* Direct channels — bottom-left on desktop, after image on mobile */}
                        <div
                            data-contact-left
                            className="md:col-span-7 md:col-start-1 md:row-start-2"
                        >
                            <p className="mb-4 text-[10px] uppercase tracking-[0.35em] text-neutral-400">
                                {t('contact.channelsTitle')}
                            </p>
                            <ul className="space-y-0 divide-y divide-neutral-200">
                                <li className="py-3">
                                    <span className="block text-[10px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>
                                        {t('contact.email')}
                                    </span>
                                    <a
                                        href="mailto:oppermann-kunst@web.de"
                                        className="mt-1 block text-sm text-neutral-700 underline-offset-4 hover:underline"
                                    >
                                        oppermann-kunst@web.de
                                    </a>
                                </li>
                                <li className="py-3">
                                    <span className="block text-[10px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>
                                        {t('contact.instagram')}
                                    </span>
                                    <a
                                        href="https://www.instagram.com/donataoppermann"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-1 block text-sm text-neutral-700 underline-offset-4 hover:underline"
                                    >
                                        @donataoppermann
                                    </a>
                                </li>
                                <li className="py-3">
                                    <span className="block text-[10px] uppercase tracking-[0.3em]" style={{ color: GOLD }}>
                                        {t('contact.location')}
                                    </span>
                                    <a
                                        href="https://www.google.com/maps/place/Brunnenstra%C3%9Fe+17A,+92318+Neumarkt+in+der+Oberpfalz/@49.2752229,11.3553606,27454m/data=!3m1!1e3!4m6!3m5!1s0x479f7aa98f498a41:0xc3bf1051fc471dc5!8m2!3d49.2838353!4d11.4916133!16s%2Fg%2F11kzxtkvsm?entry=ttu"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mt-1 block text-sm text-neutral-700 underline-offset-4 hover:underline"
                                    >
                                        {t('contact.addressLabel')}
                                    </a>
                                </li>
                            </ul>
                        </div>

                    </div>
                </div>
                </main>
            </div>

            <Footer />
        </>
    );
}
